@if (group) {
  <ng-container [formGroup]="group">
    <ng-container *ngTemplateOutlet="isSimple ? formFieldSimple : formFieldComplex"></ng-container>
  </ng-container>
}

@if (!group) {
  <ng-container *ngTemplateOutlet="isSimple ? formFieldSimple : formFieldComplex"></ng-container>
}

<ng-template #formFieldComplex>
  <div class="container">
    @if (icon) {
      <mat-icon [ngClass]="{ focus: input.focused }">{{ icon }}</mat-icon>
    }
    <mat-form-field color="accent">
      @if (shouldCasePipe) {
        <mat-label>{{ label | titlecase }}</mat-label>
      }
      @if (!shouldCasePipe) {
        <mat-label>{{ label }}</mat-label>
      }
      @if (usingDefaults) {
        <!-- a toggletip: "using default options" is its name and its tooltip. Its click stops here, as the clear
        button's does: the field would otherwise take focus and open its options -->
        <button
          type="button"
          class="button-reset toggletip"
          matSuffix
          [attr.aria-label]="sysTextForDefaults?.usingDefaultOptions"
          [matTooltip]="sysTextForDefaults?.usingDefaultOptions"
          (click)="$event.stopPropagation()">
          <mat-icon class="suffix-icon field-using-defaults-icon">settings_applications</mat-icon>
        </button>
      }
      @if (showClear && !_isDisabled && hasData() && !showSpinner) {
        <button type="button" class="button-reset" matSuffix [attr.aria-label]="sysText.clear_ariaLabel" (click)="clearInput($event)">
          <mat-icon class="suffix-icon mat-clear">clear</mat-icon>
        </button>
      }
      @if (!_isDisabled && showSearchIcon && !hasData()) {
        <mat-icon matSuffix class="suffix-icon">search</mat-icon>
      }
      @if (showSpinner) {
        <mat-spinner matSuffix [diameter]="20" color="accent" class="suffix-icon"></mat-spinner>
      }
      <input matInput [matAutocomplete]="auto" [formControl]="control" #input="matInput" autocomplete="off"/>
      <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayFn.bind(this)" (optionSelected)="onOptionSelect()">
        @for(option of filteredSelectionOptions | async; track option){
          <mat-option [value]="option.emailAddressTypeId == null ? option.id : option.id + '.' + option.emailAddressTypeId">
            <div>
              @if (option.id == defaultId) {
                <!-- inside an option it can't be a button: its meaning is hidden text, read with the option -->
                <mat-icon class="option-using-defaults-icon" matTooltip="{{sysTextForDefaults?.defaultOption}}">settings_applications</mat-icon>
                <span class="cdk-visually-hidden">{{ sysTextForDefaults?.defaultOption }}</span>
              }
              @if (showImage) {
                <img
                  [src]="option.image || 'assets/images/missing_avatar.svg'"
                  alt=""
                  class="profile-image" />
              }
              <span>{{ option.description }}</span>
              @if (option.relevantContextId) {
                <span> &bull;&nbsp;{{sysText["relevantContext" + option.relevantContextId]}}</span>
              }
              @if (option.emailAddressTypeId) {
                <span> &bull;&nbsp;{{sysText["emailAddressType" + option.emailAddressTypeId]}}</span>
              }
            </div>
            <div [ngClass]="{ 'leave-option-info': showImage, 'leave-option-info-noimage': !showImage }">
              @if (option.companyName) {
                <span class="caption option-extras"> &bull;&nbsp;{{option.companyName}}</span>
              }
              @if (option.isSystem == true) {
                <span class="caption option-extras"> &bull;&nbsp;{{"-- " + sysText.system + " --"}}</span>
              }
              @if (option.isStock == true) {
                <span class="caption option-extras"> &bull;&nbsp;{{"-- " + sysText.stock + " --"}}</span>
              }
              @if (option.workEmail) {
                <span class="caption option-extras"> &bull;&nbsp;{{option.workEmail}}</span>
              }
              @if (option.personalEmail) {
                <span class="caption option-extras"> &bull;&nbsp;{{option.personalEmail}}</span>
              }
              @if (option.emailAddress) {
                <span class="caption option-extras"> &bull;&nbsp;{{option.emailAddress}}</span>
              }
              @if (option.optionInfo) {
                <span class="caption option-extras"> &bull;&nbsp;{{option.optionInfo}}</span>
              }
            </div>
          </mat-option>
        }
      </mat-autocomplete>
      <mat-hint [ngClass]="{ highlight: isHintHighlighted, warn: isHintWarning }" align="end">{{ hint }}</mat-hint>
      @if (!_isDisabled && hasClickableHint) {
        <mat-hint align="start">
          @if (shouldCasePipe) {
            <span>{{ interfaceName | capitalizefirst }}</span>
          }
          @if (!shouldCasePipe) {
            <span>{{ interfaceName }}</span>
          }
          {{ sysText.notFound | lowercase }}?
          <button type="button" class="button-reset pointer" (click)="onHintClick()">
            {{ sysText.addANew | capitalizefirst }}
            @if (shouldCasePipe) {
              <span>{{ secondaryInterfaceName || interfaceName | lowercase }}</span>
            }
            @if (!shouldCasePipe) {
              <span>{{ secondaryInterfaceName || interfaceName }}</span>
            }.
          </button>
        </mat-hint>
      } @else if (hasClickableHint) {
        <mat-hint align="start">
          <button type="button" class="button-reset pointer" (click)="onHintClick()">
            {{ sysText.cancelNew | capitalizefirst }}
            @if (shouldCasePipe) {
              <span>{{ secondaryInterfaceName || interfaceName | lowercase }}</span>
            }
            @if (!shouldCasePipe) {
              <span>{{ secondaryInterfaceName || interfaceName }}</span>
            }.
          </button>
        </mat-hint>
      }
      @if (control.errors && !control.errors?.msError && hasClickableHint) {
        <mat-error>
          {{ sysText.pleaseChooseAnExisting | capitalizefirst }}
          @if (shouldCasePipe) {
            <span>{{ interfaceName | lowercase }}</span>
          }
          @if (!shouldCasePipe) {
            <span>{{ interfaceName }}</span>
          }
          {{ sysText.or | lowercase }}
          <button type="button" class="button-reset pointer" (click)="onHintClick()">
            {{ sysText.addANew | lowercase }}
            @if (shouldCasePipe) {
              <span>{{ secondaryInterfaceName || interfaceName | lowercase }}</span>
            }
            @if (!shouldCasePipe) {
              <span>{{ secondaryInterfaceName || interfaceName }}</span>
            }.
          </button>
        </mat-error>
      }
      @if (control.errors && !control.errors?.msError && !hasClickableHint) {
        <mat-error>
          {{ sysText.pleaseChooseAnExisting | capitalizefirst }}
          @if (shouldCasePipe) {
            <span>{{ interfaceName | lowercase }}</span>
          }
          @if (!shouldCasePipe) {
            <span>{{ interfaceName }}</span>
          }
        </mat-error>
      }
      @if (msErrorKey != null || control.errors?.msError != null) {
        <mat-error>
          {{ control.errors?.msError == msErrorKey ? customMsError : control.errors?.msError }}
          <!-- attach add new link to msError coming from back-end -->
          @if (comboMsError) {
            <button type="button" class="button-reset pointer" (click)="onHintClick()">
              {{ sysText.addANew | lowercase }}
              @if (shouldCasePipe) {
                <span>{{ secondaryInterfaceName || interfaceName | lowercase }}</span>
              }
              @if (!shouldCasePipe) {
                <span>{{ secondaryInterfaceName || interfaceName }}</span>
              }.
            </button>
          }
        </mat-error>
      }
    </mat-form-field>
  </div>
  @if (!_isDisabled && hasClickableHint && showHintAndError && control.errors) {
    <div class="custom-mat-hint">
      @if (shouldCasePipe) {
        <span>{{ interfaceName | capitalizefirst }}</span>
      }
      @if (!shouldCasePipe) {
        <span>{{ interfaceName }}</span>
      }
      {{ sysText.notFound | lowercase }}?
      <button type="button" class="button-reset pointer" (click)="onHintClick()">
        {{ sysText.addANew | capitalizefirst }}
        @if (shouldCasePipe) {
          <span>{{ secondaryInterfaceName || interfaceName | lowercase }}</span>
        }
        @if (!shouldCasePipe) {
          <span>{{ secondaryInterfaceName || interfaceName }}</span>
        }.
      </button>
    </div>
  }
</ng-template>

<ng-template #formFieldSimple>
  <div class="container">
    <mat-icon [ngClass]="{ focus: input.focused }">{{ icon }}</mat-icon>
    <mat-form-field color="accent">
      <mat-label>{{ label | titlecase }}</mat-label>
      @if (usingDefaults) {
        <button
          type="button"
          class="button-reset toggletip"
          matSuffix
          [attr.aria-label]="sysTextForDefaults?.usingDefaultOptions"
          [matTooltip]="sysTextForDefaults?.usingDefaultOptions"
          (click)="$event.stopPropagation()">
          <mat-icon class="suffix-icon field-using-defaults-icon">settings_applications</mat-icon>
        </button>
      }
      @if (!_isDisabled && showSearchIcon) {
        <mat-icon matSuffix class="suffix-icon">search</mat-icon>
      }
      <input matInput [matAutocomplete]="auto" [formControl]="control" #input="matInput" autocomplete="off"/>
      <mat-autocomplete #auto="matAutocomplete">
        @for (option of filteredStringOptions | async; track option) {
          <mat-option [value]="option">
            {{ option }}
          </mat-option>
        }
      </mat-autocomplete>
      <mat-hint [ngClass]="{ highlight: isHintHighlighted, warn: isHintWarning }" align="end">{{ hint }}</mat-hint>
      @if (control.errors?.msError) {
        <mat-error>{{ control.errors?.msError }}</mat-error>
      }
      @if (control.errors?.required) {
        <mat-error>{{ sysText.required | capitalizefirst }}</mat-error>
      }
    </mat-form-field>
  </div>
</ng-template>
